/**
 * Король гори: one cube, and no way up that does not cost you something.
 *
 * A hundred blocks on a side, every one of them a вибухоблок, every one of
 * them looking exactly like every other. Three in ten go off when you break
 * them. There is nothing to mine, nothing to build and nothing to fight - the
 * whole map is the decision of which block to hit next, and the only way to
 * find out is to hit it.
 *
 * **Which blocks explode is decided by where they are, not by a die.**
 *
 * That matters more than it looks. A roll at break time would be a different
 * roll on every screen: two people mining the same wall would blow different
 * holes in it, which is the failure the whole server design exists to
 * prevent. Rolling only on the server would fix that and cost a round trip
 * before anything visible happened.
 *
 * Hashing the position instead makes the answer the same everywhere and for
 * ever: the same block in the same world always was the one that goes off,
 * whether anybody has touched it or not. It survives a restart, it survives
 * being saved and loaded, and it can be tested without a socket.
 *
 * It also means a tunnel you dug once is a tunnel you can dig again. That is
 * the right trade for a race up a cube: what you learn is worth something.
 */

/** How wide, deep and tall the hill is. */
export const HILL_SPAN = 100;

/**
 * How thick the stone floor under it is, and so where the hill starts.
 *
 * Here rather than in the generator because two things need to agree about
 * it: the code that lays the floor, and the code that decides how far off the
 * ground somebody is. They were two numbers in two files for about an hour.
 */
export const HILL_FLOOR = 2;

/**
 * Three in twenty.
 *
 * It was three in ten, which is one live block in every three you touch:
 * digging anywhere was a coin toss you lost more often than the map was
 * worth. Half of that is still often enough that nobody digs without
 * thinking, and rare enough that a climb is a climb rather than a series of
 * explosions with a person in the middle.
 */
export const BOOM_CHANCE = 0.15;

/**
 * How far a blast reaches, in blocks.
 *
 * Three is a hole you climb into rather than a scratch, and small enough that
 * one unlucky block does not open a shaft to the ground. It is also - the
 * point of the whole map - enough to take the floor out from under whoever is
 * standing on it.
 */
export const BLAST_RADIUS = 3;

/** What a blast does to somebody standing in the middle of it. */
export const BLAST_DAMAGE = 9;

/**
 * Whether the block at these coordinates is one of the three in ten.
 *
 * A hash, not a random number: see the note at the top of this file. Every
 * coordinate is mixed in with a large odd multiplier and the result avalanched
 * so that neighbours do not correlate - the first attempt used a cheaper mix
 * and produced visible diagonal stripes of live blocks, which is a map that
 * teaches you the wrong thing.
 */
export function explodes(x: number, y: number, z: number, seed: number): boolean {
  let h = (seed | 0) ^ 0x9e3779b9;
  h = Math.imul(h ^ (x | 0), 0x85ebca6b);
  h = Math.imul(h ^ (y | 0), 0xc2b2ae35);
  h = Math.imul(h ^ (z | 0), 0x27d4eb2f);
  h ^= h >>> 15;
  h = Math.imul(h, 0x2545f491);
  h ^= h >>> 13;
  // Unsigned, then into 0..1. `>>> 0` before dividing, or every other block
  // comes out negative and nothing ever explodes.
  return (h >>> 0) / 4294967296 < BOOM_CHANCE;
}

/**
 * How long a round lasts once everybody has had a minute to gather.
 *
 * Five minutes, and it is the round rather than a hold: whoever is highest
 * when it runs out has won it. Getting up there is one lucky tunnel, and
 * still being up there at the end while three other people dig at the block
 * under you is the map.
 *
 * It was a hold - five minutes on top *in a row*, reset by every fall - and
 * that is a different game: one where being knocked off at four minutes
 * costs the whole thing and there is no coming back inside a round. A round
 * anybody can still win in the last ten seconds is the one that was asked
 * for.
 */
export const CLIMB_SECONDS = 300;

/**
 * And how long everybody has to arrive before it starts.
 *
 * Nobody can move during it. A minute is long enough to open the page,
 * choose a козак and read the two lines about what this map is, and short
 * enough that whoever was ready first is not left wondering whether it is
 * broken.
 */
export const GATHER_SECONDS = 60;

/** How long one whole round takes, gathering and climbing together. */
export const ROUND_SECONDS = GATHER_SECONDS + CLIMB_SECONDS;

/** Which half of the round it is. */
export type RoundPhase = "gather" | "climb";

/** Where the round is now. */
export interface RoundState {
  readonly phase: RoundPhase;
  /** Seconds until this phase ends. Counts down, never below zero. */
  readonly left: number;
  /** Whether anybody may move. Nobody may, while everybody is gathering. */
  readonly frozen: boolean;
}

/**
 * The round, worked out from one clock.
 *
 * A function of the time since it began rather than a machine with states in
 * it: the server and the page both have to answer this several times a
 * second, and two machines stepping separately are two machines disagreeing
 * about which half it is the moment one of them drops a frame.
 */
export function roundAt(
  elapsed: number,
  gather = GATHER_SECONDS,
  /**
   * How long the climb lasts.
   *
   * A knob for the same reason `gather` is one: a check that has to wait six
   * minutes to see the end of a round is a check nobody runs. It was not a
   * knob at first, so `?round=6` shortened the *round* and left the banner
   * counting down from five minutes over it - which is a countdown that does
   * not match the game it is counting.
   */
  climb = CLIMB_SECONDS,
): RoundState {
  const since = Math.max(0, elapsed);
  if (since < gather) {
    return { phase: "gather", left: gather - since, frozen: true };
  }
  return {
    phase: "climb",
    left: Math.max(0, gather + climb - since),
    frozen: false,
  };
}

/**
 * How far above the floor you have to be before you are on the hill at all.
 *
 * Without this the crown went to whoever was highest full stop - so a player
 * standing alone at the spawn, on the flat, was crowned for existing. Five
 * blocks is past the first course of the cube and cannot be reached by
 * standing anywhere on the floor, which is what makes it mean "you have
 * climbed something".
 */
export const KING_MIN_HEIGHT = 5;

/** The lowest a body can be and still be counted. */
export const KING_MIN_Y = HILL_FLOOR + KING_MIN_HEIGHT;

/**
 * Whoever is highest, of the people who are off the ground at all.
 *
 * Height alone above that, with no test for whether they are on the hill: a
 * player who has tunnelled *inside* the cube and is higher than the one on
 * the summit really is higher, and inventing a rule that says otherwise would
 * be a rule nobody can see from inside the game. There is nothing to build
 * with on this map - вибухоблок drops nothing - so height is honest.
 *
 * Ties go to nobody: two people on one block should not flicker the crown
 * between them fifteen times a second.
 */
export function highest<T extends { readonly id: number; readonly y: number }>(
  players: readonly T[],
  minY = KING_MIN_Y,
): T | null {
  let best: T | null = null;
  let tied = false;
  for (const player of players) {
    if (player.y < minY) continue;
    if (best === null || player.y > best.y) { best = player; tied = false; }
    else if (player.y === best.y) tied = true;
  }
  return tied ? null : best;
}

export interface BlastCell {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/**
 * Every cell a blast takes out, nearest first.
 *
 * A ball, not a cube: a cube of twenty-seven blocks reads as a room somebody
 * carved, and the corners are further from the charge than the faces are.
 * Nearest first so a caller that has to stop early - a cap on how much may
 * change in one tick - takes the middle out rather than the rim.
 */
export function blastCells(
  x: number, y: number, z: number, radius = BLAST_RADIUS,
): BlastCell[] {
  const out: { cell: BlastCell; away: number }[] = [];
  for (let dx = -radius; dx <= radius; dx++) {
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dz = -radius; dz <= radius; dz++) {
        const away = Math.hypot(dx, dy, dz);
        if (away > radius) continue;
        out.push({ cell: { x: x + dx, y: y + dy, z: z + dz }, away });
      }
    }
  }
  out.sort((a, b) => a.away - b.away);
  return out.map((o) => o.cell);
}

/**
 * What the blast does to somebody that far from it.
 *
 * Falls off to nothing at the edge, so standing at arm's length is a fright
 * and standing on it is most of your health. Never negative: a caller that
 * passed a distance beyond the radius would otherwise be *healed* by an
 * explosion, which is the kind of arithmetic nobody notices until somebody
 * farms it.
 */
export function blastHurt(away: number, radius = BLAST_RADIUS): number {
  if (away >= radius) return 0;
  return Math.max(0, Math.round(BLAST_DAMAGE * (1 - away / radius)));
}
